/**
 * Real-time Exchange Rates Utility for Sentlo SafePay
 * Integrates with ExchangeRate-API (Free Tier)
 */

export interface ExchangeRate {
  base: string;
  rates: Record<string, number>;
  lastUpdated: string;
}

// Initial fallback rates
export let LATEST_RATES: ExchangeRate = {
  base: 'NGN',
  rates: {
    'NGN': 1,
    'USD': 0.00067, 
    'GBP': 0.00053, 
    'GHS': 0.0084,  
    'EUR': 0.00062
  },
  lastUpdated: new Date().toISOString()
};

/**
 * Fetches the latest exchange rates from an external API
 */
export async function fetchLatestRates(base: string = 'NGN'): Promise<ExchangeRate> {
  // Check if we are in a browser environment
  if (typeof window === 'undefined') return LATEST_RATES;

  try {
    // Using ExchangeRate-API (Keyless Open Endpoint)
    // This allows basic currency fetching without an API key for demo purposes.
    const response = await fetch(`https://open.er-api.com/v6/latest/${base}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.result === 'success') {
      const newRates: ExchangeRate = {
        base: data.base_code,
        rates: data.rates,
        lastUpdated: data.time_last_update_utc
      };
      
      LATEST_RATES = newRates;
      return newRates;
    }
    
    return LATEST_RATES;
  } catch (err) {
    // If the network request fails (e.g. offline or CORS/Sandbox), we use the high-quality fallbacks
    // and log a more descriptive warning instead of a raw TypeError
    console.warn('Sentlo: Unable to fetch live exchange rates. Using secure cached rates.', err instanceof Error ? err.message : '');
    return LATEST_RATES;
  }
}

/**
 * Converts an amount from one currency to another
 */
export function convertCurrency(amount: number, from: string, to: string): number {
  if (from === to) return amount;

  // Convert to NGN base first (or whatever our primary base is)
  // If base is from, it's already in base. If not, we normalize.
  const fromRate = LATEST_RATES.rates[from] || 1;
  const toRate = LATEST_RATES.rates[to] || 1;

  // Amount in base currency (if base is NGN)
  // formula: amount_in_target = (amount / rate_of_from) * rate_of_to
  return (amount / fromRate) * toRate;
}

/**
 * Formats a value with its currency symbol
 */
export function formatCurrency(amount: number, currency: string): string {
  const symbols: Record<string, string> = {
    'NGN': '₦',
    'USD': '$',
    'GBP': '£',
    'GHS': 'GH₵',
    'EUR': '€'
  };

  const symbol = symbols[currency] || '$';
  return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Gets a estimated cross-border transaction fee (Sentlo SafePay Fee)
 */
export function calculateSafePayFee(amount: number, from: string, to: string): number {
  const baseFeePercent = 0.015; // 1.5% base fee
  const crossBorderSurcharge = from !== to ? 0.01 : 0; // Additional 1% for cross-border
  return amount * (baseFeePercent + crossBorderSurcharge);
}
